export type GeoIPContext =
    | {
          ip?: string
          error: string
      }
    | {
          ip: string
          country: {
              name: string
              iso_code: string
          }
          region: {
              name: string | null
              iso_code: string | null
          }
          city: string | null
          postal_code: string | null
      }

export const RegionTargets = [
    'ON', // Ontario
    'NS', // Nova Scotia
    'PE', // Prince Edward Island
    'NB' // New Brunswick
]

const CACHE_KEY_PREFIX = 'clutch_geo_context_cache'
const CACHE_TIMESTAMP_KEY_PREFIX = 'clutch_geo_context_timestamp'
// eslint-disable-next-line no-magic-numbers
const CACHE_DURATION = 60 * 60 * 1000 // 1 hour in milliseconds

const getRequestKey = (
    regionTargets: string[],
    ipOverride: string | null
): string => {
    const baseKey = regionTargets.sort().join('-')
    return ipOverride ? `${baseKey}-ip-${ipOverride}` : baseKey
}

const getCacheKey = (
    regionTargets: string[],
    ipOverride: string | null
): string => {
    return `${CACHE_KEY_PREFIX}-${getRequestKey(regionTargets, ipOverride)}`
}

const getTimestampKey = (
    regionTargets: string[],
    ipOverride: string | null
): string => {
    return `${CACHE_TIMESTAMP_KEY_PREFIX}-${getRequestKey(
        regionTargets,
        ipOverride
    )}`
}

const getStoredCache = (
    regionTargets: string[],
    ipOverride: string | null
): GeoIPContext | undefined => {
    try {
        const cacheKey = getCacheKey(regionTargets, ipOverride)
        const timestampKey = getTimestampKey(regionTargets, ipOverride)
        const cached = localStorage.getItem(cacheKey)
        const timestamp = localStorage.getItem(timestampKey)

        if (!cached || !timestamp) {
            return
        }

        if (Date.now() - Number(timestamp) >= CACHE_DURATION) {
            localStorage.removeItem(cacheKey)
            localStorage.removeItem(timestampKey)
            return
        }

        return JSON.parse(cached)
    } catch (e) {
        return
    }
}

const makeRequest = async (
    regionTargets: string[],
    ipOverride: string | null
): Promise<GeoIPContext | undefined> => {
    const apiUrl = ipOverride
        ? `/api-root/geo/?ip_override=${encodeURIComponent(ipOverride)}`
        : '/api-root/geo/'

    const response = await fetch(apiUrl)
    if (!response.ok) {
        return
    }

    const result: GeoIPContext = await response.json()

    if ('error' in result || !result.region.iso_code) {
        return
    }

    if (!regionTargets.includes(result.region.iso_code)) {
        return
    }

    const cacheKey = getCacheKey(regionTargets, ipOverride)
    const timestampKey = getTimestampKey(regionTargets, ipOverride)
    localStorage.setItem(cacheKey, JSON.stringify(result))
    localStorage.setItem(timestampKey, String(Date.now()))

    return result
}

/**
 * Fetches and validates geographical context data based on IP address.
 *
 * @description
 * This function retrieves geographical information based on the user's IP address or an optional
 * IP override provided via URL query parameter. It validates the response against a list of
 * supported regions (default: RegionTargets or custom extendedRegionTargets). Results are cached
 * for 1 hour in localStorage, with separate caches maintained for different combinations of
 * region target sets and IP overrides.
 *
 * @remarks
 * The function will return undefined in the following cases:
 * - The API request fails
 * - The response contains an error
 * - No region is detected
 * - The detected region is not in the provided region targets list
 *
 * Cache and request deduplication are handled based on both:
 * - The specific region targets being checked
 * - The IP override value (if any) from URL parameters
 *
 * @param {string[]} [extendedRegionTargets] - Optional array of region codes to validate against,
 *                                            defaults to RegionTargets if not provided
 *
 * @returns {Promise<GeoIPContext | undefined>}
 * Returns a Promise that resolves to:
 * - GeoIPContext object if successful and region is supported
 * - undefined if any validation fails
 *
 * @example
 * // Using default RegionTargets
 * const geoContext = await getClutchGeoContext();
 * if (geoContext) {
 *   console.log('Valid region:', geoContext.region.iso_code);
 * }
 *
 * @example
 * // Using custom region targets
 * const geoContext = await getClutchGeoContext(['ON', 'BC', 'AB']);
 * if (geoContext) {
 *   console.log('Valid region:', geoContext.region.iso_code);
 * }
 */
export const getClutchGeoContext = async (
    extendedRegionTargets?: string[]
): Promise<GeoIPContext | undefined> => {
    const urlParams = new URLSearchParams(window.location.search)
    const noCache = urlParams.get('nocache')
    const ipOverride = urlParams.get('ip_override')
    const regionTargets = extendedRegionTargets || RegionTargets

    const cachedResult = getStoredCache(regionTargets, ipOverride)
    if (cachedResult && !noCache) {
        return cachedResult
    }

    return makeRequest(regionTargets, ipOverride)
}
